import React, { useRef, useCallback } from 'react';
import api from '../lib/api';
import { Button } from './ui/button';
import { Printer } from 'lucide-react';
import { toast } from 'sonner';

export default function ReceiptPrint({ orderId, size = 'sm' }) {
  const iframeRef = useRef(null);

  const handlePrint = useCallback(async () => {
    try {
      const { data } = await api.get(`/orders/${orderId}/receipt`);
      const { order, table, location, eftpos_slip } = data;
      const date = new Date(order.created_at);
      const dateStr = date.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
      const timeStr = date.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' });

      const itemsHtml = order.items.map(item =>
        `<tr>
          <td style="text-align:left;padding:2px 0;">${item.quantity}x ${item.name}</td>
          <td style="text-align:right;padding:2px 0;">$${item.total.toFixed(2)}</td>
        </tr>`
      ).join('');

      // If the order was closed via the manual `Paid (Card)` flow, print the
      // EFTPOS slip / auth code so end-of-night reconciliation matches each
      // line to the terminal roll at a glance.
      const eftposHtml = eftpos_slip ? `
        <div class="row" style="font-size:11px;"><span>EFTPOS slip</span><span>${(eftpos_slip.reference || '—').toString().slice(0, 32)}</span></div>
      ` : '';

      const html = `<!DOCTYPE html>
<html><head><style>
  @page { margin: 0; size: 80mm auto; }
  * { margin: 0; padding: 0; box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; color-adjust: exact; }
  body { font-family: 'Courier New', monospace; width: 80mm; padding: 4mm; color: #000; font-size: 13px; font-weight: 700; line-height: 1.4; }
  .center { text-align: center; }
  .brand { font-size: 24px; font-weight: 900; letter-spacing: 4px; margin-bottom: 2px; color: #000; }
  .sub { font-size: 11px; color: #000; font-weight: 600; margin-bottom: 8px; }
  .divider { border-top: 2px dashed #000; margin: 6px 0; }
  .row { display: flex; justify-content: space-between; font-size: 13px; padding: 1px 0; color: #000; font-weight: 700; }
  table { width: 100%; border-collapse: collapse; font-size: 13px; color: #000; font-weight: 700; }
  .total-row { font-weight: 900; font-size: 16px; color: #000; }
  .footer { font-size: 11px; color: #000; font-weight: 600; margin-top: 10px; text-align: center; }
</style></head><body>
  <div class="center">
    <div class="brand">Majnu</div>
    <div class="sub">${location.name}</div>
    ${location.address ? `<div class="sub">${location.address}</div>` : ''}
  </div>
  <div class="divider"></div>
  <div class="row"><span>Table: ${table ? table.number : order.table_number}</span><span>Order: #${order.id.slice(0, 8)}</span></div>
  <div class="row"><span>${dateStr}</span><span>${timeStr}</span></div>
  <div class="row"><span>Customer: ${order.customer_name || 'Guest'}</span></div>
  <div class="divider"></div>
  <table>${itemsHtml}</table>
  <div class="divider"></div>
  ${(order.discount_amount && order.discount_amount > 0) ? `
    <div class="row"><span>Subtotal</span><span>$${Number(order.subtotal ?? order.total).toFixed(2)}</span></div>
    <div class="row"><span>${(order.discount_label || 'Discount').toString()}</span><span>-$${Number(order.discount_amount).toFixed(2)}</span></div>
  ` : ''}
  <div class="row total-row"><span>TOTAL</span><span>$${order.total.toFixed(2)}</span></div>
  <div class="row"><span>Payment</span><span>${(order.payment_method || '').toUpperCase()}</span></div>
  <div class="row"><span>Status</span><span>${(order.payment_status || '').replace('_', ' ').toUpperCase()}</span></div>
  ${eftposHtml}
  <div class="divider"></div>
  <div class="footer">
    Thank you for visiting Majnu<br/>
    We hope to see you again soon
  </div>
</body></html>`;

      const iframe = iframeRef.current;
      const doc = iframe.contentDocument || iframe.contentWindow.document;
      doc.open();
      doc.write(html);
      doc.close();
      setTimeout(() => { iframe.contentWindow.focus(); iframe.contentWindow.print(); }, 300);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load receipt');
    }
  }, [orderId]);

  return (
    <>
      <iframe ref={iframeRef} style={{ display: 'none', position: 'absolute', width: 0, height: 0 }} title="receipt" />
      <Button
        size={size}
        onClick={handlePrint}
        className="h-7 bg-gold/10 text-gold border border-gold/20 hover:bg-gold/20 rounded-sm font-sans text-xs"
        data-testid={`print-receipt-${orderId}`}
      >
        <Printer className="w-3 h-3 mr-1" /> Receipt
      </Button>
    </>
  );
}
